import { AdAnalyticsFilter, AdAnalyticsReport } from '@kaizenplatform/hugot-entity';
import gql from 'graphql-tag';
import { List } from 'immutable';

import GraphQLClient from '../../adapter/GraphQLClient';

class AdAnalyticsReportWebApi {
  private readonly graphqlClient: GraphQLClient;
  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public getAdAnalyticsReportsList = async ({
    organizationId,
  }: {
    organizationId?: string;
  }): Promise<List<AdAnalyticsReport>> => {
    const result = await this.graphqlClient.query(
      gql`
        query adAnalyticsReports($organizationId: ID!) {
          adAnalyticsReports(organizationId: $organizationId) {
            edges {
              node {
                id
                adVideos {
                  adVideoId
                }
                createdBy {
                  id
                  name
                }
                createdAt
                title
                body
                period
                platforms {
                  key
                  name
                }
                medias {
                  key
                  name
                }
                placements {
                  key
                  name
                }
              }
            }
            pageInfo {
              endCursor
              hasNextPage
              hasPreviousPage
              startCursor
              totalCount
            }
          }
        }
      `,
      {
        organizationId: organizationId,
      },
    );
    if (!Array.isArray(result.adAnalyticsReports.edges)) {
      throw new Error('result.adAnalyticsReports.edges must be an array.');
    }
    return List(result.adAnalyticsReports.edges.map((edge: any) => AdAnalyticsReport.fromJSON(edge.node)));
  };

  public getAdAnalyticsReport = async ({ id }: { id?: string }): Promise<AdAnalyticsReport> => {
    const result = await this.graphqlClient.query(
      gql`
        query adAnalyticsReport($id: ID!) {
          adAnalyticsReport(id: $id) {
            id
            adVideos {
              adVideoId
            }
            createdBy {
              id
              name
            }
            createdAt
            title
            body
            period
            platforms {
              key
              name
            }
            medias {
              key
              name
            }
            placements {
              key
              name
            }
          }
        }
      `,
      {
        id: id,
      },
    );
    return result.adAnalyticsReport;
  };

  public createReport = async ({
    adVideoIds,
    organizationId,
    title,
    period,
    platformKey,
    mediaKey,
    placementKey,
  }: AdAnalyticsFilter & {
    title: string;
    adVideoIds: string[] | null;
    organizationId?: string;
  }): Promise<string> => {
    const resurt = await this.graphqlClient.query(
      gql`
        mutation createAdAnalyticsReport(
          $adVideoIds: [ID!]!
          $organizationId: ID!
          $title: String!
          $period: PerformanceReportPeriodPattern!
          $adPlatformKeys: [AdPlatformKey!]!
          $adMediaKeys: [AdMediaKey!]!
          $adPlacementKeys: [AdPlacementKey!]!
        ) {
          createAdAnalyticsReport(
            adVideoIds: $adVideoIds
            organizationId: $organizationId
            title: $title
            period: $period
            adPlatformKeys: $adPlatformKeys
            adMediaKeys: $adMediaKeys
            adPlacementKeys: $adPlacementKeys
          ) {
            id
            title
          }
        }
      `,
      {
        adPlatformKeys: platformKey !== undefined ? platformKey : [],
        adMediaKeys: mediaKey !== undefined ? mediaKey : [],
        adPlacementKeys: placementKey !== undefined ? placementKey : [],
        period: period,
        adVideoIds: adVideoIds,
        organizationId: organizationId,
        title: title,
      },
    );

    return resurt.createAdAnalyticsReport.id;
  };

  public deleteReport = async ({ id }: { id: string }): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation deleteAdAnalyticsReport($id: ID!) {
          deleteAdAnalyticsReport(id: $id)
        }
      `,
      { id },
    );
    return;
  };

  public updateReport = async ({ id, body }: { id: string; body: string }): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation updateAdAnalyticsReport($id: ID!, $body: String!) {
          updateAdAnalyticsReport(id: $id, body: $body)
        }
      `,
      { id, body },
    );
    return;
  };
}

export default AdAnalyticsReportWebApi;
